import type { TPesCatalogPageType, TPesPathnamePageType } from './types/router';
import { pesPathnamePageTypeToPageTypeMap } from './constants';

type TPesURLParts = {
  languageCode: string;
  countryCode: string;
  pageTypeRaw: TPesPathnamePageType;
  pageType: TPesCatalogPageType;
  pageId?: string;
  pageSEOName?: string;
};

class PesURL extends URL {
  countryCode: string;
  languageCode: string;
  pageTypeRaw: TPesPathnamePageType;
  pageType: TPesCatalogPageType;
  pageId?: string;
  pageSEOName?: string;

  constructor(url: string, base?: string) {
    super(url, base);

    const pesPathnameParsed = this.getPathnameParsed();

    this.countryCode = pesPathnameParsed.countryCode;
    this.languageCode = pesPathnameParsed.languageCode;
    this.pageTypeRaw = pesPathnameParsed.pageTypeRaw;
    this.pageType = pesPathnameParsed.pageType;
    this.pageId = pesPathnameParsed.pageId;
    this.pageSEOName = pesPathnameParsed.pageSEOName;
  }

  // TODO: double check what kinds of URLs we should parse. It seems like there may be any character in a url segment.
  urlRegexps = {
    allProducts: /(?<countryCode>\w+)\/(?<languageCode>\w+)(?:\/v3)?\/(?<pageTypeRaw>(?:[\w-])+)\//,
    product:
      /(?<countryCode>\w+)\/(?<languageCode>\w+)(?:\/v3)?\/(?<pageTypeRaw>(?:[\w-])+)\/(?<pageId>[\w.-]+)(?:\/(?<pageSEOName>.+))?\//,
    rangeCategorySubcategory:
      /(?<countryCode>\w+)\/(?<languageCode>\w+)(?:\/v3)?\/(?<pageTypeRaw>(?:[\w-])+)\/(?<pageId>[\w.]+)(?:-(?<pageSEOName>.+))?\//,
  };

  getPageTypeProcessed = (pageTypeTaw: string): TPesCatalogPageType => {
    return pesPathnamePageTypeToPageTypeMap[pageTypeTaw];
  };

  get normalizedPathname() {
    let { pathname } = this;

    // force pathname of chinese website to have the same shape as all others locales
    if (this.origin.endsWith('.cn') || this.pathname.startsWith('/zh')) {
      pathname = `/cn${this.pathname}`;
    }

    if (!pathname.endsWith('/')) {
      pathname = `${pathname}/`;
    }

    return pathname;
  }

  getProcessedURLParts = (): TPesURLParts => {
    let parsed: RegExpExecArray;

    // So far, we handle only the following cases:
    // - all products `/ww/en/all-product/`
    // - category `/ww/en/product-category/{category_id}/`
    // - sub-category `/ww/en/product-subcategory/{subcategory_id}/`
    // - range `/ww/en/product-range/{range_id}/`
    // - product `/ww/en/product/{product_id}/`
    //
    // There are some other types of pages that we don't handle yet:
    // - business `/ww/en/work/products/`
    // - site `/ww/en/`
    if (this.normalizedPathname.includes('all-products')) {
      parsed = this.urlRegexps.allProducts.exec(this.normalizedPathname);
    } else if (this.normalizedPathname.includes('product-')) {
      parsed = this.urlRegexps.rangeCategorySubcategory.exec(this.normalizedPathname);
    } else if (this.normalizedPathname.includes('product')) {
      parsed = this.urlRegexps.product.exec(this.normalizedPathname);
    }

    if (!parsed.groups) {
      throw new RangeError("Can't handle this URL.");
    }

    return parsed.groups as TPesURLParts;
  };

  getPathnameParsed = (): TPesURLParts => {
    const parts = this.getProcessedURLParts();

    const { countryCode, languageCode, pageTypeRaw, pageId, pageSEOName } = parts;

    return {
      countryCode,
      languageCode,
      pageTypeRaw,
      pageType: pageTypeRaw ? this.getPageTypeProcessed(pageTypeRaw) : undefined,
      pageId,
      pageSEOName,
    };
  };
}

type TParseCommonURLToRouterPartsOptions = {
  prefix?: string;
};

export const parseCommonURLToRouterParts = (
  urlRaw: string,
  options?: TParseCommonURLToRouterPartsOptions,
): TPesURLParts => {
  const { prefix } = options || {};

  let url = urlRaw;

  if (prefix) {
    url = url.replace(prefix, '');
  }

  let pesUrl: PesURL;

  if (!url.startsWith('http')) {
    const { origin: base } = window.location;
    pesUrl = new PesURL(url, base);
  } else {
    pesUrl = new PesURL(url);
  }

  const { countryCode, languageCode, pageTypeRaw, pageType, pageId, pageSEOName } = pesUrl;

  return {
    countryCode,
    languageCode,
    pageTypeRaw,
    pageType,
    pageId,
    pageSEOName,
  };
};
